import { Autocomplete, AutocompleteProps, TextField, TextFieldProps } from "@mui/material"
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form"
import { useTranslation } from "react-i18next";

interface FormAutocompleteProps<IFormValues extends FieldValues = FieldValues, T extends any = any, Multiple extends (boolean | undefined) = undefined, DisableClearable extends (boolean | undefined) = undefined, FreeSolo extends (boolean | undefined) = undefined>
    extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'ref' | 'value' | 'renderInput'> {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    label?: string,
    required?: boolean,
    textFieldProps?: Omit<TextFieldProps, 'name' | 'value' | 'label' | 'error'>
}

const FormAutocomplete = <IFormValues extends FieldValues, T extends any = any, Multiple extends boolean | undefined = boolean, DisableClearable extends boolean | undefined = boolean, FreeSolo extends boolean | undefined = boolean>({
    name,
    control,
    label,
    required,
    textFieldProps,
    ...props
}: FormAutocompleteProps<any, T, Multiple, DisableClearable, FreeSolo>) => {
    const {t} = useTranslation(['network', 'translation']);
    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            render={({ field, fieldState, formState }) =>
                <Autocomplete
                    {...props}
                    openOnFocus
                    autoHighlight
                    autoSelect
                    ref={field.ref}
                    value={field.value}
                    onChange={(e, value, reason, details) => {
                        field.onChange(value);
                        props.onChange?.(e, value, reason, details);
                    }}
                    onBlur={(e) => {
                        field.onBlur();
                        props.onBlur?.(e);
                    }}
                    disabled={props.disabled ?? formState.isSubmitting}
                    fullWidth
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            label={label}
                            required={required}
                            error={!!fieldState.error}
                            helperText={t(!!fieldState.error?.message ? fieldState.error.message : '') ?? textFieldProps?.helperText}
                            fullWidth
                        />
                    } />
            }
        />
    )
};

export default FormAutocomplete;